<script lang="ts">
  import { onMount } from "svelte"
  import { flip } from "svelte/animate"
  import Sortable from "sortablejs"
  import {
    toast,
    xhr,
    drag_enabled,
    sortElementsForSortable,
    wait
  } from "$app/utils"
  import Heading from "$app/components/Heading.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  interface Props {
    goals: GoalType[]
    rootGoalId: number
    headings: string[]
    editable: boolean
    onopenModal: (goal: GoalType) => void
    onupdateGoals: (goals: GoalType[]) => void
    onupdateHeadings: (headings: any[]) => void
  }

  let props = $props()
  let {
    rootGoalId,
    headings,
    editable,
    onopenModal,
    onupdateGoals,
    onupdateHeadings
  }: Props = props
  let goals: GoalType[] = $state(props.goals)

  let dnd_enabled = editable

  let innerWidth = $state(0)

  onMount(() => {
    dnd_enabled = drag_enabled(innerWidth, editable)
    setSortable()
  })

  const flipDurationMs = 300

  function showGoalLinkage(goal: GoalType) {
    const annualGoals = document.querySelectorAll(
      `[data-parent-goal-id='${goal.id}']`
    )
    annualGoals.forEach((annualGoal) => {
      annualGoal.classList.add("bg-indigo-50")
      const annualGoalId = annualGoal.dataset.goalId

      const quarterlyGoals = document.querySelectorAll(
        `[data-parent-goal-id='${annualGoalId}']`
      )
      quarterlyGoals.forEach((quarterlyGoal) => {
        quarterlyGoal.classList.add("bg-indigo-50")
      })
    })
  }

  function hideGoalLinkage(goal: GoalType) {
    const annualGoals = document.querySelectorAll(
      `[data-parent-goal-id='${goal.id}']`
    )
    annualGoals.forEach((annualGoal) => {
      annualGoal.classList.remove("bg-indigo-50")
      const annualGoalId = annualGoal.dataset.goalId

      const quarterlyGoals = document.querySelectorAll(
        `[data-parent-goal-id='${annualGoalId}']`
      )
      quarterlyGoals.forEach((quarterlyGoal) => {
        quarterlyGoal.classList.remove("bg-indigo-50")
      })
    })
  }

  async function saveSortOrder(newGoals: GoalType[]): Promise<void> {
    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/goals/sort_by_parent.json`,
        { id: rootGoalId, goals: newGoals.map((s) => s.id) }
      )
      onupdateGoals(resp.data.goals)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the sort order", "error")
      const tmpGoals = [...goals]
      goals = []
      await wait(50)
      goals = tmpGoals
    }
  }

  let sortableSection: HTMLElement

  function setSortable(): void {
    if (sortableSection) {
      Sortable.create(sortableSection, {
        group: "LongTermGoals",
        animation: 200,
        delay: 400,
        delayOnTouchOnly: true,
        direction: "vertical",
        ghostClass: "opacity-50",
        dragClass: "border-green-400",
        onEnd: async function (e: EventSortType) {
          if (e.newIndex == e.oldIndex) return
          this.options.sort = false

          try {
            await saveSortOrder(
              sortElementsForSortable(goals, e.newIndex, e.oldIndex)
            )
          } catch (e) {
          } finally {
            this.options.sort = true
          }
        }
      })
    }
  }
</script>

<svelte:window bind:innerWidth />

<div class="bg-white shadow border-l-4 border-long-term-goal">
  <div class="bg-white px-4 py-4 sm:px-6 relative">
    <div class="flex items-center justify-start md:justify-center">
      <h3 class="leading-6 font-semibold text-black">
        <Heading
          title="Long-Term Goals"
          section="LongTermGoal"
          {headings}
          {onupdateHeadings}
        />
      </h3>
    </div>
  </div>

  {#if goals.length === 0}
    <div class="flex justify-center items-center w-full my-10">
      <span
        class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
      >
        No Long-Term Goals
      </span>
    </div>
  {/if}
  <div class="flex flex-col border-t-1 border-gray-200">
    <ul class="divide-y divide-gray-200" bind:this={sortableSection}>
      {#each goals as goal, idx (goal.id)}
        <li
          animate:flip={{ duration: flipDurationMs }}
          class="block hover:bg-indigo-100 {editable
            ? 'hover:cursor-move'
            : ''}"
          data-goal-id={goal.id}
          onfocus={() => showGoalLinkage(goal)}
          onmouseover={() => showGoalLinkage(goal)}
          onmouseout={() => hideGoalLinkage(goal)}
          onblur={() => hideGoalLinkage(goal)}
          ondblclick={() => onopenModal(goal)}
        >
          <div class="pl-2 pr-0 py-1 sm:pl-4">
            <div class="flex items-center justify-between select-none">
              <p
                class="text-sm font-medium text-black hover:text-indigo-600 truncate select-none"
              >
                <span class="text-gray-400 text-xs">{idx + 1}.</span>
                {goal.name}
              </p>

              <div class="h-8"></div>
            </div>
          </div>
        </li>
      {/each}
    </ul>
  </div>
</div>
